import { TopSheet, useTopSheetMotion } from "@-/dev.improve.haniverse.components.ui.top-sheet";
import { FlashcardScrollView } from "@-/dev.improve.haniverse.components.ui.card-scroll-view";
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { MorphemeMnemonicsMeaningViewDecompositionSection } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning._sections.decomposition";
import { MorphemeMnemonicsMeaningViewMnemonicSection } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning._sections.mnemonic";
import {
  MorphemeMnemonicsMeaningViewAddNoteButton,
  MorphemeMnemonicsMeaningViewNotesSection,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning._sections.notes";
import { MorphemeMnemonicsHeroGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content._components.hero-glyph";
import {
  MorphemeMnemonicsMeaningViewImageSection,
  useMorphemeMnemonicsMeaningViewImageSectionGate,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning._components.hero-section";
import {
  MorphemeMnemonicsMeaningViewTitleSectionRenderer,
  useMorphemeMnemonicsMeaningViewTitleSectionGate,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning._sections.title";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Surface, SurfaceContent } from "@-/dev.improve.haniverse.components.ui.surface";
import { MorphemeDescriptionRenderer } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.description";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { parseGraphemeFromDescriptionLinkUrl } from "@-/dev.improve.haniverse.lib.morpheme.description";
import { useGraphemeLinkPress } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.preview-sheet";
import { MorphemeMnemonicsMeaningViewContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ScrollToBottomButton } from "@-/dev.improve.haniverse.components.ui.scroll-to-bottom-button";
import { Pressable, ScrollView, View } from "@-/com.npmjs.react-native";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import type { useMorphemeMnemonicsMeaningViewTabScroll } from "#use-morpheme-mnemonics-meaning-view-tab-scroll";
import { useHasMorphemeMnemonicsMeaningAsComponent } from "#use-has-morpheme-mnemonics-meaning-as-component";

/** The sheet's `h-8` drag handle, which overlays the content just below the summary. */
const SHEET_HANDLE_HEIGHT = 32;

function MorphemeMnemonicsMeaningViewAsComponentSection() {
  const [description, mnemonicTerms, grapheme, final] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (context) => context.readonlySlice.data.description?.trim() ?? "",
    (context) => context.readonlySlice.data.mnemonicTerms,
    (context) => context.readonlySlice.data.grapheme,
    (context) => context.readonlySlice.data.final,
  );
  const { morphemeHrefObject, openMnemonicUsagesSheet } = useContext(
    MorphemeMnemonicsMeaningViewContext,
  );
  const onGraphemeLinkPress = useGraphemeLinkPress({ prefix: morphemeHrefObject });
  // A final grapheme is a learning leaf: no other mnemonic uses it, so there is nothing to list.
  const hasMnemonicUsages = final !== true;
  const handleLinkPress = useCallback(
    ({ url }: { url: string }) => {
      // `[company](.)` names this grapheme's own component mnemonic.
      if (url === "." || parseGraphemeFromDescriptionLinkUrl(url) === grapheme) {
        if (hasMnemonicUsages) {
          openMnemonicUsagesSheet();
        }
        return;
      }
      const linkedGrapheme = parseGraphemeFromDescriptionLinkUrl(url);
      if (linkedGrapheme !== null) {
        onGraphemeLinkPress(linkedGrapheme);
      }
    },
    [grapheme, hasMnemonicUsages, onGraphemeLinkPress, openMnemonicUsagesSheet],
  );

  // A header-less surface inset 4px from the sheet edges.
  return description === "" && mnemonicTerms.length === 0 ? null : (
    <Surface className="mx-1" testID="meaning-as-component">
      <SurfaceContent className="gap-2 p-3">
        <View className="flex-row items-baseline justify-between gap-3">
          {hasMnemonicUsages && mnemonicTerms.length > 0 ? (
            <Pressable
              accessibilityHint={`Shows the mnemonics that use ${grapheme} as a component`}
              accessibilityLabel={mnemonicTerms.join(", ")}
              accessibilityRole="button"
              className="min-w-0 flex-1"
              hitSlop={8}
              onPress={openMnemonicUsagesSheet}
              testID="meaning-as-component-mnemonic-terms"
            >
              <Text className="text-sm text-foreground italic underline">
                {mnemonicTerms.join(", ")}
              </Text>
            </Pressable>
          ) : (
            <Text className="min-w-0 flex-1 text-sm text-foreground italic">
              {mnemonicTerms.join(", ")}
            </Text>
          )}
          <Text className="shrink-0 text-[10px] font-medium tracking-widest text-muted-foreground">
            AS COMPONENT
          </Text>
        </View>
        {description !== "" ? (
          <MorphemeDescriptionRenderer
            content={description}
            onLinkPress={handleLinkPress}
            size="sm"
          />
        ) : null}
      </SurfaceContent>
    </Surface>
  );
}

/**
 * The summary sheet holds the centred glyph, label and gloss, then the mnemonic sentence and the
 * decomposition beneath it; the page below keeps only the illustration.
 */
export function MorphemeMnemonicsMeaningViewTabSections({
  layout,
}: {
  layout: ReturnType<typeof useMorphemeMnemonicsMeaningViewTabScroll>;
}) {
  const hasAsComponent = useHasMorphemeMnemonicsMeaningAsComponent();
  const titleSectionGate = useMorphemeMnemonicsMeaningViewTitleSectionGate();
  const imageSectionGate = useMorphemeMnemonicsMeaningViewImageSectionGate();
  const [grapheme] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (context) => context.readonlySlice.data.grapheme,
  );
  const [isExpanded, setIsExpanded] = useRouteBodyState("meaning-sheet-expanded", false);
  const [hasCustomNote, setHasCustomNote] = useState(false);
  const [availableHeight, setAvailableHeight] = useState(600);
  const [summaryHeight, setSummaryHeight] = useState(64);
  const [belowFoldHeight, setBelowFoldHeight] = useState(0);
  const sheetScroll = useRef<ScrollView>(null);
  const scrollOffset = useSharedValue(0);
  const sections = layout.sectionsLayout;
  // The compact summary runs from the glyph through the decomposition (plus any saved note).
  // Collapsing can only hide what follows it.
  const totalHeight = summaryHeight + belowFoldHeight;
  // A very long summary uses the sheet's scroller once its viewport fills the available screen.
  const maximumViewport = Math.max(
    0,
    availableHeight - 32 - layout.contentContainerStyle.paddingBottom,
  );
  const compactHeight = Math.min(summaryHeight, maximumViewport);
  const expandedHeight = Math.max(compactHeight, Math.min(totalHeight, maximumViewport));
  const position = isExpanded ? 1 : 0;
  const motion = useTopSheetMotion({
    maximum: expandedHeight - compactHeight,
    onPositionChange: (next) => setIsExpanded(next === 1),
    position,
    scrollOffset,
  });
  useEffect(() => {
    if (sections.notesEditing.isEditing) {
      setIsExpanded(true);
    }
  }, [sections.notesEditing.isEditing, setIsExpanded]);
  useEffect(() => {
    if (!isExpanded && !sections.notesEditing.isEditing) {
      sheetScroll.current?.scrollTo({ animated: false, y: 0 });
      scrollOffset.set(0);
    }
  }, [isExpanded, sections.notesEditing.isEditing, scrollOffset]);
  // The collapsed sheet still shows the strip beneath its frosted handle. Content past the summary
  // stays invisible there, appearing only once the sheet opens (or scrolls) past that strip, so the
  // fold always sits above it.
  const belowFoldStyle = useAnimatedStyle(() => ({
    opacity: interpolate(
      motion.value.get() + scrollOffset.get(),
      [0, SHEET_HANDLE_HEIGHT],
      [0, 1],
      Extrapolation.CLAMP,
    ),
  }));
  return (
    <View
      className="min-h-0 flex-1"
      onLayout={(event) => setAvailableHeight(event.nativeEvent.layout.height)}
      testID="meaning-learn-view"
    >
      <TopSheet
        handle={
          <Pressable
            accessibilityActions={[
              { label: "Expand character summary", name: "increment" },
              { label: "Collapse character summary", name: "decrement" },
            ]}
            accessibilityHint="Swipe down to show component details and add a custom note."
            accessibilityLabel="Character learning sheet"
            accessibilityRole="adjustable"
            accessibilityValue={{ max: 1, min: 0, now: position }}
            className="h-8 items-center justify-center"
            onAccessibilityAction={(event) =>
              setIsExpanded(event.nativeEvent.actionName === "increment")
            }
            onPress={() => setIsExpanded((current) => !current)}
            testID="meaning-sheet-handle"
          >
            <View className="h-0.75 w-8 rounded-full bg-muted-foreground/20" />
          </Pressable>
        }
        motion={motion}
        testID="meaning-top-sheet"
        viewportInset={compactHeight}
      >
        <ScrollView
          bounces={false}
          contentContainerClassName="pb-8"
          keyboardShouldPersistTaps="handled"
          nestedScrollEnabled
          onScroll={(event) => scrollOffset.set(event.nativeEvent.contentOffset.y)}
          ref={sheetScroll}
          scrollEnabled={layout.scrollEnabled && (isExpanded || summaryHeight > compactHeight)}
          scrollEventThrottle={16}
          scrollIndicatorInsets={{ bottom: 32 }}
          testID="meaning-sheet-scroll"
        >
          <View
            className="pb-2"
            onLayout={(event) => setSummaryHeight(event.nativeEvent.layout.height)}
            testID="meaning-sheet-summary"
          >
            {/* The glyph stands alone, centred; the label and heading follow beneath it. */}
            <View className="items-center gap-1 px-3 pt-2.25 pb-2" testID="meaning-title-block">
              <MorphemeMnemonicsHeroGlyph />
              <View className="w-full min-w-0" testID="meaning-title-column">
                {titleSectionGate === null ? null : (
                  <MorphemeMnemonicsMeaningViewTitleSectionRenderer gate={titleSectionGate} />
                )}
              </View>
            </View>
            {/* The mnemonic sentence reads straight off the sheet; only the decomposition below it
                sits in a surface, inset 4px from the sheet edges like the component details. */}
            <View className="min-w-0 px-3 pb-2" testID="meaning-sheet-mnemonic">
              <MorphemeMnemonicsMeaningViewMnemonicSection />
            </View>
            <MorphemeMnemonicsMeaningViewDecompositionSection className="mx-1" />
            {/* Stays mounted to report whether a note exists; spaced only while it shows one. */}
            <View
              className={hasCustomNote || sections.notesEditing.isEditing ? "px-3 pt-2" : undefined}
              testID="meaning-sheet-notes"
            >
              <MorphemeMnemonicsMeaningViewNotesSection
                editing={sections.notesEditing}
                inline
                morpheme={grapheme}
                onContentPresenceChange={setHasCustomNote}
                showAddButton
              />
            </View>
          </View>
          <Animated.View
            accessibilityElementsHidden={!isExpanded}
            importantForAccessibility={isExpanded ? "auto" : "no-hide-descendants"}
            onLayout={(event) => setBelowFoldHeight(event.nativeEvent.layout.height)}
            pointerEvents={isExpanded ? "auto" : "none"}
            style={belowFoldStyle}
            testID="meaning-sheet-below-fold"
          >
            <View testID="meaning-sheet-component">
              {hasAsComponent ? <MorphemeMnemonicsMeaningViewAsComponentSection /> : null}
            </View>
            {hasCustomNote || sections.notesEditing.isEditing ? null : (
              <MorphemeMnemonicsMeaningViewAddNoteButton onPress={sections.notesEditing.onStart} />
            )}
          </Animated.View>
        </ScrollView>
      </TopSheet>
      {/* Match Details: expanded content overlays the page; the summary reserves its own space. */}
      <View
        className="min-h-0 flex-1"
        style={{ paddingTop: Math.min(availableHeight, compactHeight + 40) }}
        testID="meaning-mnemonic-view"
      >
        <FlashcardScrollView
          className="flex-1"
          contentContainerStyle={layout.contentContainerStyle}
          contentOffset={layout.initialContentOffset}
          keyboardShouldPersistTaps="handled"
          nestedScrollEnabled
          onContentSizeChange={layout.handleContentSizeChange}
          onLayout={layout.handleLayout}
          onScroll={layout.handleScroll}
          ref={layout.scrollViewRef}
          scrollEnabled={layout.scrollEnabled}
          scrollEventThrottle={16}
          showsScrollToBottomButton={false}
          testID="meaning-mnemonic-scroll"
        >
          <View className="gap-3">
            {sections.showsShowMore ? (
              <ScrollToBottomButton onPress={sections.onShowMore} placement="inline" />
            ) : null}
            {imageSectionGate === null ? null : (
              <MorphemeMnemonicsMeaningViewImageSection
                gate={imageSectionGate}
                imageHeight={sections.image.height}
                imageWidth={sections.image.width}
                isLoaded={sections.image.isLoaded}
                mode={sections.image.mode}
                onError={sections.image.onError}
                onLayout={sections.image.onLayout}
                onLoad={sections.image.onLoad}
                onPress={sections.image.onPress}
                showsExpandHint={sections.image.showsExpandHint}
              />
            )}
          </View>
        </FlashcardScrollView>
      </View>
    </View>
  );
}
